import { waterTankThresholdColorScale } from '@weenat/client/dist/core/dss/irrigation'
import { useConvertedValue } from '@weenat/client/dist/hooks'

import { useClient, useQuery } from '@weenat/client'
import { CheckInWindowMinutes, DangerThresholdMinutes } from '@weenat/client/dist/enums/Devices'
import { LatestData } from '@weenat/client/dist/resources/latest'
import { Plot } from '@weenat/client/dist/resources/plots'
import {
  WaterTankThresholdType,
  waterTankThresholdTypes
} from '@weenat/client/dist/resources/thresholds.types'
import { useIntl } from '@weenat/wintl'
import { PlotListItemsVariants } from 'app/src/dashboard/components/DashboardMap/plotListItem/PlotListItemMetricSummary'
import { PREVENT_DRAG_CLASSNAME } from 'app/src/dashboard/components/plots/dashboards/constants'
import useDisclosure from 'app/src/hooks/useDisclosure'
import logEvent from 'app/utils/analytics'
import { clamp, isEmpty, isNil, isNumber, max } from 'lodash-es'
import moment from 'moment-timezone'
import { transparentize } from 'polished'
import { memo } from 'react'
import { styled, useTheme } from 'styled-components'
import ChartLegendItem from '../charts/ChartLegendItem'
import DelimitedFlex from '../DelimitedFlex'
import EmptyStateWithRipple from '../empty-state/EmptyStateWithRipple'
import Icons from '../Icons'
import Modal from '../Modal'
import Text from '../Text'
import UpdateInformation from '../UpdateInformation'
import WeenatPressable from '../WeenatPressable'
import WeedgetContextMenu from './ContextMenu'
import { IrrigationRestrictionWeedget } from './IrrigationRestrictionWeedget'
import WeedgetCard from './WeedgetCard'
import WeedgetHeader from './WeedgetHeader'
import WeedgetLoading from './WeedgetLoading'

const CHART_LEGEND_STYLE = { gap: 12 }

const BAR_CONTAINER_HEIGHT = 32
const BAR_PROFILE_CAROUSEL_HEIGHT = 16
const BAR_PROFILE_WIDGET_HEIGHT = 12

const HORIZON_COLUMN_WIDTH = 48
const VALUE_COLUMN_WIDTH = 64

interface ReservoirProfileWeedgetProps {
  plot: Plot
  onDelete?: () => void
  isRestricted?: boolean
}

const ProfileBarContainer = styled(Flex)`
  flex: 1;
  position: relative;
  justify-content: center;
  align-items: center;
  height: 100%;
  overflow: hidden;
  background-color: transparent;

  & > * {
    z-index: 1;
  }
`

const ProfileCursor = styled(Flex)<{ $percentageValue: number; $variant?: PlotListItemsVariants }>`
  position: absolute;
  height: ${(p) =>
    2 * (p.$variant === 'tooltip' ? BAR_PROFILE_WIDGET_HEIGHT : BAR_PROFILE_CAROUSEL_HEIGHT)}px;
  width: ${(p) => (p.$variant === 'tooltip' ? 8 : 10)}px;
  background-color: ${(p) => p.theme.colors.grayscale.white};
  border-color: ${(p) => p.theme.colors.grayscale.black};
  border-style: solid;
  border-width: 2px;
  border-radius: ${(p) => (p.$variant === 'tooltip' ? p.theme.radiuses.sm : p.theme.radiuses.md)}px;
  top: 50%;
  transform: translate(-50%, -50%);
  left: ${(p) => clamp(0, p.$percentageValue, 100)}%;
  -webkit-filter: ${(p) => `drop-shadow(
    ${p.theme.shadows.sm.shadowOffset.width}px 
    ${p.theme.shadows.sm.shadowOffset.height}px 
    ${p.theme.shadows.sm.shadowRadius}px 
    ${transparentize(1 - p.theme.shadows.sm.shadowOpacity, p.theme.shadows.sm.shadowColor)})`};
  filter: ${(p) => `drop-shadow(
    ${p.theme.shadows.sm.shadowOffset.width}px 
    ${p.theme.shadows.sm.shadowOffset.height}px 
    ${p.theme.shadows.sm.shadowRadius}px 
    ${transparentize(1 - p.theme.shadows.sm.shadowOpacity, p.theme.shadows.sm.shadowColor)})`};
`

const WaterTankLegend: FC<{ waterTankKey: WaterTankThresholdType }> = ({ waterTankKey }) => {
  const { t } = useIntl()
  const thresholdColor = waterTankThresholdColorScale(waterTankKey)
  return (
    <ChartLegendItem
      color={thresholdColor}
      labelSize='sm'
      label={t(`water_tank_profile.legend.${waterTankKey}`)}
      itemSize={12}
      style={CHART_LEGEND_STYLE}
    />
  )
}

const ProfileLegend: FC<{ maxValue: number | null; ticksNumber: number }> = ({
  maxValue,
  ticksNumber
}) => {
  const ticks = !isNil(maxValue)
    ? Array.from({ length: ticksNumber }, (_, i) => (i * maxValue) / (ticksNumber - 1))
    : []

  return (
    <Flex
      $width={'100%'}
      $backgroundColor={'grayscale.white'}
      $position='sticky'
      $bottom={0}
      $zIndex={1}
      $px='md'
    >
      <Flex $width={HORIZON_COLUMN_WIDTH} />
      <Flex $position='relative' $flex={1} height={'fit-content'} $justifyContent='space-between'>
        {ticks.map((value, i, arr) => {
          return (
            <Flex key={value} $position='relative' $flexDirection='column'>
              <Flex $height={6} $width={1} $backgroundColor={'grayscale.black'} />
              <Flex $width={1}>
                <Text
                  $fontSize={i === 0 || i === arr.length - 1 ? 'sm' : 'xs'}
                  $textAlign='center'
                  style={{ transform: 'translateX(-50%)' }}
                >
                  {Math.floor(value)}
                </Text>
              </Flex>
            </Flex>
          )
        })}
      </Flex>
      <Box $width={VALUE_COLUMN_WIDTH} />
    </Flex>
  )
}

export const ProfileBar: FC<{
  thresholds: { [key in WaterTankThresholdType]: number }
  value?: number
  maxCapacity: number | null
  variant?: PlotListItemsVariants
}> = memo(({ thresholds, value, maxCapacity, variant = 'tooltip' }) => {
  const { radiuses } = useTheme()

  const toPercentageOfMax = (_value?: number) =>
    !isNil(_value) && !isNil(maxCapacity) ? (Math.min(_value, maxCapacity) * 100) / maxCapacity : 0

  const percentageValue = toPercentageOfMax(value)

  const filteredThresholds = Object.entries(thresholds).filter(
    ([, threhsholdValue]) => threhsholdValue > 0
  )

  return (
    <ProfileBarContainer>
      <Flex $mx='md' $flex={1} $position='relative'>
        {isEmpty(filteredThresholds) ? (
          <Flex
            $backgroundColor={'grayscale.300'}
            $height={
              variant === 'tooltip' ? BAR_PROFILE_WIDGET_HEIGHT : BAR_PROFILE_CAROUSEL_HEIGHT
            }
            $width={`100%`}
            $borderRadius={'xs'}
          />
        ) : (
          filteredThresholds.map(([thresholdName, thresholdValue], i, arr) => {
            const thresholdColor = waterTankThresholdColorScale(
              thresholdName as WaterTankThresholdType
            )

            const borderRadius =
              arr.length === 1
                ? `${radiuses.xs}px`
                : i === 0
                  ? `${radiuses.xs}px 0 0 ${radiuses.xs}px`
                  : i === arr.length - 1
                    ? `0 ${radiuses.xs}px ${radiuses.xs}px 0`
                    : '0'

            return (
              <Flex
                key={thresholdName}
                $backgroundColor={thresholdColor}
                $height={
                  variant === 'tooltip' ? BAR_PROFILE_WIDGET_HEIGHT : BAR_PROFILE_CAROUSEL_HEIGHT
                }
                $width={`${toPercentageOfMax(thresholdValue)}%`}
                $borderRadius={borderRadius}
              />
            )
          })
        )}
        {!isNil(value) && <ProfileCursor $percentageValue={percentageValue} $variant={variant} />}
      </Flex>
    </ProfileBarContainer>
  )
})

const WaterTankProfile: FC<{
  data: LatestData
}> = ({ data }) => {
  const { t } = useIntl()
  const { convertValue } = useConvertedValue()

  const horizons = Object.keys({
    ...data.SURVIVAL_WATER_TANK,
    ...data.READILY_WATER_CONTENT,
    ...data.WATER_TANK_LEVEL,
    ...data.WATER_TANK_MAX
  })

  const horizonsValue = [
    ...Object.values(data?.SURVIVAL_WATER_TANK || {}),
    ...Object.values(data?.READILY_WATER_CONTENT || {}),
    ...Object.values(data?.WATER_TANK_LEVEL || {}),
    ...Object.values(data?.WATER_TANK_MAX || {})
  ]

  const maxCapacity = max(horizonsValue)
  /* Display a 5% saturation above WATER_TANK_MAX / the WATER_TANK_LEVEL */
  const correctedMaxCapacity = !isNil(maxCapacity) ? Math.round(1.05 * maxCapacity) : null

  const horizonThresholds: Array<{ [key in WaterTankThresholdType]: number }> = horizons.map(
    (horizon) => {
      const horizonSurvivalWaterTank = data.SURVIVAL_WATER_TANK?.[horizon] ?? 0
      const horizonReadilyWaterContent = data.READILY_WATER_CONTENT?.[horizon] ?? 0
      const horizonWaterTankMax = data.WATER_TANK_MAX?.[horizon] ?? 0

      return {
        survival_water_tank: horizonSurvivalWaterTank,
        rfu: horizonReadilyWaterContent,
        saturation: (correctedMaxCapacity ?? 0) - horizonWaterTankMax
      }
    }
  )

  return (
    <Flex $flex={1} $maxHeight={'100%'} $overflow='auto'>
      <Flex
        $flex={1}
        $flexDirection='column'
        $justifyContent='center'
        $minHeight={'max-content'}
        $px='md'
      >
        <Flex $flexDirection='column' $gap='xs'>
          {horizons.map((horizon, i) => {
            return (
              <Flex
                key={horizon}
                $alignItems='center'
                $justifyContent='center'
                $height={BAR_CONTAINER_HEIGHT}
                $minHeight={BAR_CONTAINER_HEIGHT}
              >
                <Flex $width={HORIZON_COLUMN_WIDTH} $justifyContent='flex-end' $alignItems='center'>
                  <Text $fontSize='rg' $fontWeight='medium'>
                    {!isNaN(parseInt(horizon)) ? horizon : '—'} {t('units.centimeters_abbr')}
                  </Text>
                </Flex>

                <ProfileBar
                  thresholds={horizonThresholds[i]}
                  maxCapacity={correctedMaxCapacity}
                  value={
                    isNumber(data?.WATER_TANK_LEVEL?.[horizon])
                      ? clamp(0, data.WATER_TANK_LEVEL?.[horizon], 100)
                      : undefined
                  }
                />
                <Flex $width={VALUE_COLUMN_WIDTH} $justifyContent='flex-end' $alignItems='center'>
                  <Text $fontSize='rg' $fontWeight='medium' $textAlign='end'>
                    {convertValue({
                      metric: 'WATER_TANK_LEVEL',
                      displayUnit: true,
                      maximumFractionDigits: 0,
                      value: isNumber(data?.WATER_TANK_LEVEL?.[horizon])
                        ? clamp(0, data?.WATER_TANK_LEVEL?.[horizon], 100)
                        : null,
                      showEmptyState: true
                    })}
                  </Text>
                </Flex>
              </Flex>
            )
          })}
        </Flex>
        <ProfileLegend maxValue={correctedMaxCapacity} ticksNumber={6} />
      </Flex>
    </Flex>
  )
}

const ReservoirProfileWeedget: FC<ReservoirProfileWeedgetProps> = ({
  plot,
  onDelete,
  isRestricted
}) => {
  const { t } = useIntl()

  const { open, close, isOpen } = useDisclosure()

  const handleOpenLegend = () => {
    logEvent('open_md_watertank_profile')
    open()
  }

  const client = useClient()

  const { data: latestPlotData, isLoading } = useQuery(client.latest.get(plot.id, 'plots'))

  const WATER_TANK_devices = (latestPlotData?.devices ?? []).filter(
    (device) =>
      device.availableMeasures.includes('HPOT') ||
      device.availableMeasures.includes('U_CAPA') ||
      device.availableMeasures.includes('SWC')
  )

  const WATER_TANK_data = {
    WATER_TANK_LEVEL: latestPlotData?.data?.WATER_TANK_LEVEL,
    SURVIVAL_WATER_TANK: latestPlotData?.data?.SURVIVAL_WATER_TANK,
    READILY_WATER_CONTENT: latestPlotData?.data?.READILY_WATER_CONTENT,
    WATER_TANK_MAX: latestPlotData?.data?.WATER_TANK_MAX
  }

  const isDataEmpty =
    isNil(WATER_TANK_data) ||
    Object.values(WATER_TANK_data).every((value) => isNil(value) || isEmpty(value))

  const closestTimestamp = Math.max(
    ...WATER_TANK_devices.map((d) => d.datetime?.getTime()).filter((d) => !isNil(d))
  )
  const WATER_TANK_timestamp = moment(closestTimestamp).unix()
  const WATER_TANK_endName =
    isNil(WATER_TANK_devices) || isEmpty(WATER_TANK_devices)
      ? '—'
      : WATER_TANK_devices.length > 1
        ? t('sensor_profile.tensiometer_kit')
        : WATER_TANK_devices[0]?.endUserName.join(' ')

  return (
    <>
      <WeedgetCard $position='relative' $flex={1} $overflow='hidden'>
        {/*  HEADER */}
        <WeedgetHeader>
          <Flex $flexDirection='column' $flex={1} $minWidth={0}>
            <Text $fontWeight='bold' $ellipsis>
              {t('water_tank_profile.widget_title')}
            </Text>
          </Flex>
          {!isLoading && !isDataEmpty && (
            <Icons.InfoCircle
              onPress={handleOpenLegend}
              className={PREVENT_DRAG_CLASSNAME}
              $backgroundColor='grayscale.100'
              $color='grayscale.black'
              $p='sm'
              $size='lg'
            />
          )}
          <WeedgetContextMenu onDelete={onDelete} />
        </WeedgetHeader>

        {/* CONTENT */}
        {isLoading ? (
          <WeedgetLoading />
        ) : isRestricted ? (
          <IrrigationRestrictionWeedget />
        ) : !isDataEmpty ? (
          <>
            <WaterTankProfile data={WATER_TANK_data} />
            {/* FOOTER */}
            <DelimitedFlex
              $alignItems='center'
              $isDelimitedOnTop
              $p='md'
              $justifyContent='center'
              $gap='sm'
              $borderRadius={0}
            >
              <UpdateInformation
                name={WATER_TANK_endName}
                timestamp={WATER_TANK_timestamp}
                warningThreshold={CheckInWindowMinutes.irrigation}
                dangerThreshold={DangerThresholdMinutes.irrigation}
                timezone={plot.timezone}
              />
            </DelimitedFlex>
          </>
        ) : (
          <>
            <Flex
              $flex={1}
              $flexDirection='column'
              $alignItems='center'
              $justifyContent='center'
              $gap='md'
            >
              <EmptyStateWithRipple
                Icon={Icons.Circle}
                title={t('water_tank_profile.impossible_to_display_water_tank_profile')}
                description={t('water_tank_profile.no_data')}
              />
            </Flex>
            <WeenatPressable onPress={onDelete} targetFirstChild className={PREVENT_DRAG_CLASSNAME}>
              <DelimitedFlex
                $alignItems='center'
                $isDelimitedOnTop
                $p='md'
                $justifyContent='center'
                $gap='sm'
              >
                <Icons.Bin $color={'feedback.error.500'} $size='md' />
                <Text $color={'feedback.error.500'}>{t('weedget.actions.delete')}</Text>
              </DelimitedFlex>
            </WeenatPressable>
          </>
        )}
      </WeedgetCard>
      {/* MODAL */}
      {isOpen && (
        <Modal
          isOpen={isOpen}
          close={close}
          hasCloseIcon
          title={t('water_tank_profile.legend.title')}
        >
          <Flex $flexDirection='column' $gap='lg'>
            {waterTankThresholdTypes.map((key) => (
              <WaterTankLegend waterTankKey={key} key={key} />
            ))}
          </Flex>
        </Modal>
      )}
    </>
  )
}

export default ReservoirProfileWeedget
